// From the format module directly rather than the `@/utils` barrel, which
// reaches axios and uuid and would pull ESM into this module's tests.
import { DashboardResource } from '@/hooks/useDashboardResource';
import { daysBetweenApiDates, formatRupiah } from '@/utils/format';

/**
 * Whether one block inside a loaded response should be rendered as failed.
 *
 * Two ways a figure goes missing, and the card treats them identically: the
 * whole request failed, or the request succeeded and this particular block came
 * back null because the backend runs its aggregates under Promise.allSettled.
 * Either way the honest render is "gagal memuat", never a zero.
 *
 * Lives here rather than in each card file because all three blocks were
 * spelling it out independently, and a copy that drifts turns a failure back
 * into a number on one card.
 */
export function isBlockFailed<T>(
  resource: DashboardResource<T>,
  block: unknown
): boolean {
  return (
    resource.status === 'failed' ||
    (resource.status === 'loaded' && block == null)
  );
}

/**
 * The three CardStats props every dashboard stat derives from one resource.
 *
 * Six cards were spelling out `loading` / `failed` / `onRetry` by hand, and two
 * of them had already drifted into asking the question differently. One helper
 * means a card cannot accidentally report a failure as anything else.
 */
export function resourceCardProps<T>(
  resource: DashboardResource<T>,
  block: unknown,
  onRetry: () => void
): { loading: boolean; failed: boolean; onRetry: () => void } {
  return {
    loading: resource.status === 'loading',
    failed: isBlockFailed(resource, block),
    onRetry,
  };
}

/** "87.0%", or "—" when nothing was due (rate is null, never 0). */
export const formatCollectionRate = (rate: number | null): string =>
  rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;

/**
 * The rate's move against the previous period, in percentage points. Undefined
 * when either side is missing: a card with no comparison omits the badge.
 */
export const rateTrend = (
  rate: number | null | undefined,
  previousRate: number | null | undefined
): { value: number; direction: 'up' | 'down'; period: string } | undefined => {
  if (rate == null || previousRate == null) return undefined;
  const points = Math.round((rate - previousRate) * 1000) / 10;
  return {
    value: points,
    direction: points < 0 ? 'down' : 'up',
    period: 'vs periode sebelumnya',
  };
};

/**
 * "Rp1.200.000", or "−Rp300.000" when the window was overpaid.
 *
 * Deliberately NOT clamped to zero. A window where mitra prepaid more than they
 * were billed genuinely has a negative outstanding, and flattening it to "Rp0"
 * tells ops there is nothing left to collect when what actually happened is the
 * opposite. Same failure as rendering a failed fetch as a number.
 */
export const formatOutstanding = (amount: number): string =>
  amount < 0 ? `\u2212${formatRupiah(Math.abs(amount))}` : formatRupiah(amount);

/** "+5", "−3", "0" — sign always shown, and never colour alone. */
export const formatSignedCount = (value: number): string => {
  if (value > 0) return `+${value}`;
  // U+2212 minus, not a hyphen: it aligns with the digits and reads as a sign.
  if (value < 0) return `−${Math.abs(value)}`;
  return '0';
};

/**
 * How the repo-stuck card says its age. `null` means the rental is stuck but
 * has no history row to anchor on — it still counts, so say the count is real
 * and the age is not known, rather than printing "0 hari".
 */
export const formatStuckAge = (oldestDays: number | null): string =>
  oldestDays === null ? 'umur tidak diketahui' : `tertua ${oldestDays} hari`;

/**
 * True when a scheduled slot has already passed and the unit is still waiting.
 * These sort to the top of the queue: they are the rows that need a phone call,
 * and before the queue widened backwards they appeared on no screen at all.
 */
export const isPastDue = (
  scheduledDate: string | null | undefined,
  today: string
): boolean => Boolean(scheduledDate && scheduledDate < today);

/** "Terlambat 3 hari", for the pill on a past-due row. */
export const formatOverdueBy = (scheduledDate: string, today: string): string =>
  `Terlambat ${daysBetweenApiDates(scheduledDate, today)} hari`;
